/**
 * Locale-driven units. Browser- and server-safe (no platform APIs beyond Intl).
 *
 * There is no shipped Intl API that reports a locale's measurement system, so
 * we derive the region from the locale and map it. The default is metric /
 * Celsius / 24-hour: when the region is absent or unknown we do NOT guess US.
 *
 * Callers pass the locale they have (`navigator.language` on the client, a
 * stored preference or Accept-Language on the server). Volume/weight unit
 * conversion (cups, oz) is intentionally out of scope here because most of that
 * copy lives in CMS content, not in code.
 */
// Everyday-temperature-in-Fahrenheit regions. The US dominates; the rest are
// small territories. Everyone else reads Celsius.
const FAHRENHEIT_REGIONS = new Set(["US", "BS", "BZ", "KY", "PW", "FM", "MH", "LR"]);
// US-customary length/volume regions for everyday use.
const US_CUSTOMARY_REGIONS = new Set(["US", "LR", "MM"]);
function regionOf(locale) {
    if (!locale)
        return null;
    try {
        // Explicit region only; we don't maximize() so a bare "en" stays metric
        // rather than silently resolving to en-US.
        const region = new Intl.Locale(locale).region;
        return region ? region.toUpperCase() : null;
    }
    catch {
        return null;
    }
}
export function usesFahrenheit(locale) {
    const region = regionOf(locale);
    return region ? FAHRENHEIT_REGIONS.has(region) : false;
}
export function measurementSystem(locale) {
    const region = regionOf(locale);
    return region && US_CUSTOMARY_REGIONS.has(region) ? "us" : "metric";
}
/**
 * Format a Celsius value in the viewer's preferred temperature unit. An
 * explicit `system` (the user's stored preference) wins over the locale
 * guess, so all of a user's devices agree.
 */
export function formatTemperatureC(celsius, locale, system) {
    const fahrenheit = system ? system === "us" : usesFahrenheit(locale);
    if (fahrenheit) {
        return `${Math.round((celsius * 9) / 5 + 32)}°F`;
    }
    return `${Math.round(celsius)}°C`;
}
/** True when the locale's convention is a 24-hour clock. */
export function prefers24HourClock(locale) {
    try {
        const cycle = new Intl.DateTimeFormat(locale ?? undefined, {
            hour: "numeric",
        }).resolvedOptions().hourCycle;
        return cycle === "h23" || cycle === "h24";
    }
    catch {
        return false;
    }
}
//# sourceMappingURL=units.js.map